import { CoursesApi } from '@deckee/api-app-main';
import {
  AsyncThunkPayloadCreator,
  createAction,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { debounce } from 'throttle-debounce';
import { AppAsyncThunkAPI } from '../..';
import apiConfiguration from '../../../backend/apiConfiguration';
import { captureEvent } from '../../../event-capture';
import { CourseCategoryModel, CourseModel } from './models';
import { selectCourseSearchTerm } from './selectors';

const api = new CoursesApi(apiConfiguration);

export const setSelectedCourseCategory = createAction(
  '[Courses] Set selected course category',
  (id: string | undefined) => ({
    payload: id,
  }),
);

export const setFetchingCourses = createAction(
  '[Courses] set fetching courses',
  (fetching = true) => ({ payload: fetching }),
);

export const setSearchResults = createAction(
  '[Courses] set course search results',
  (courses: CourseModel[]) => ({
    payload: courses,
  }),
);

export const addCourses = createAction(
  '[Courses] set courses',
  (courses: CourseModel[]) => ({
    payload: courses,
  }),
);

export const setCourseCategories = createAction(
  '[Courses] set course categories',
  (categories: CourseCategoryModel[]) => ({
    payload: categories,
  }),
);

export const setCourseSearchTerm = createAction(
  '[Courses] set course search term',
  (term: string) => ({
    payload: term,
  }),
);

// NOTE: We were previously setting the selected category in here, but once the debouncing was added it was best to add elsewhere
const searchCoursesHandler: AsyncThunkPayloadCreator<
  void,
  { category?: string | undefined; searchTerm?: string | undefined },
  AppAsyncThunkAPI
> = async (searchParams, { dispatch, getState, signal, rejectWithValue }) => {
  const searchTerm = selectCourseSearchTerm(getState());

  if (searchTerm) {
    captureEvent({
      type: 'courses: search for course',
      payload: {
        searchTerm,
      },
    });
  }

  signal.throwIfAborted();

  try {
    const { data: courses } = await api.getCourses(
      {
        categoryId: searchParams.category,
        searchTerm,
      },
      {
        signal,
      },
    );
    dispatch(setSearchResults(courses));
  } catch (e: any) {
    if (!signal.aborted) {
      dispatch(setFetchingCourses(false));
      throw rejectWithValue(e.message);
    }
  }

  dispatch(setFetchingCourses(false));
};

const debouncedSearchCourses = debounce(250, searchCoursesHandler);

export const searchCourses = createAsyncThunk<
  void,
  { category?: string; searchTerm?: string },
  AppAsyncThunkAPI
>('[Courses] Search for courses', (...r) => {
  // Gross
  r[1].dispatch(setFetchingCourses());
  debouncedSearchCourses(...r);
});

export const fetchCourseCategories = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>('[Courses] fetch course categories', async (_, { dispatch }) => {
  const { data: categories } = await api.getCourseCategories();
  dispatch(setCourseCategories(categories));
});
